/**
 * Frame builder: replays the engine over a stored HandRow's action log to
 * produce a sequence of snapshots (`Frame[]`). Each frame represents one
 * user-visible "step" — either a single player action or a street
 * advance — so the replay UI can step forward/back freely.
 *
 * Because the engine is pure + deterministic given seed + actions, we
 * do NOT persist per-step snapshots. We reconstruct on demand from the
 * HH text stored on the row.
 */
import { createPokersolverEvaluator } from '@/engine/handEvaluator';
import type { HandEvaluator } from '@/engine/ports';
import { startHand, step } from '@/engine/reducer';
import { createRng } from '@/engine/rng';
import type { Action, HandState, Player } from '@/engine/types';
import type { HandRow } from '@/storage/db';
import { parseHand } from '@/storage/pokerstars';

export type Frame = {
  /** 0-indexed: 0 = initial (cards dealt, blinds posted, no action yet). */
  step: number;
  state: HandState;
  /** The action that produced this frame, or undefined at step 0. */
  lastAction?: Action;
  /** True when the engine advanced the street as part of this step. */
  streetBoundary?: boolean;
};

/** Hydrate the HandRow into engine-ready Players keyed by the HH record. */
function hydratePlayers(
  seats: ReadonlyArray<{
    seat: number;
    playerId: string;
    startStack: number;
  }>,
): Player[] {
  return seats.map((s) => ({
    id: s.playerId,
    seat: s.seat as Player['seat'],
    stack: s.startStack,
    folded: false,
    allIn: false,
    handContribution: 0,
    streetContribution: 0,
    rebuyUsed: false,
    eliminated: false,
  }));
}

export type BuildFramesOptions = {
  evaluator?: HandEvaluator;
};

/**
 * Replay the hand into an ordered frame list.
 *
 * Throws if the action log is inconsistent (e.g., corrupted row).
 */
export function buildFrames(row: HandRow, options: BuildFramesOptions = {}): Frame[] {
  const record = parseHand(row.pokerstarsHH);
  const evaluator = options.evaluator ?? createPokersolverEvaluator();
  const players = hydratePlayers(record.seats);
  const handRng = createRng(record.seed);

  const { state: initial } = startHand({
    handNumber: record.handNumber,
    level: record.level,
    seed: record.seed,
    button: record.button,
    players,
    rng: handRng,
  });

  const frames: Frame[] = [{ step: 0, state: initial }];
  const services = { rng: createRng(`${record.seed}:replay-svc`), evaluator };

  let cur = initial;
  let stepIdx = 1;
  for (const action of record.actions) {
    const prevStreet = cur.betting.street;
    const result = step(cur, action, services);
    cur = result.state;
    const streetBoundary = cur.betting.street !== prevStreet;
    frames.push({ step: stepIdx, state: cur, lastAction: action, streetBoundary });
    stepIdx += 1;
  }
  return frames;
}

/** Count only hero-authored action frames (excludes initial + opponent turns). */
export function heroActionFrameIndices(frames: ReadonlyArray<Frame>, heroId: string): number[] {
  const out: number[] = [];
  for (let i = 1; i < frames.length; i++) {
    const f = frames[i];
    if (f?.lastAction?.playerId === heroId) out.push(i);
  }
  return out;
}
